import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';

export interface UserPermissions {
  isSuperAdmin: boolean;
  isOwner: boolean;
  canCreateWorkspaces: boolean;
  canManageAllWorkspaces: boolean;
  canViewAllWorkspaces: boolean;
  canManageWorkspace: (workspaceId: string) => boolean;
  canViewWorkspace: (workspaceId: string) => boolean;
  canInviteMembers: (workspaceId: string) => boolean;
  canManageModules: (workspaceId: string) => boolean;
  managedWorkspaceIds: string[];
  accessibleWorkspaceIds: string[];
  hasFullAccess: boolean;
  roles: string[];
  checkPermission: (permission: string) => boolean;
}

export function usePermissions(): {
  permissions: UserPermissions | null;
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useAuth();

  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['user-permissions', user?.id],
    queryFn: async (): Promise<UserPermissions> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      // Check if user is super admin (owner)
      const { data: ownerCheck, error: ownerError } = await supabase
        .rpc('is_owner');

      if (ownerError) {
        console.error('Error checking owner status:', ownerError);
      }

      const isSuperAdmin = ownerCheck || false;

      // Get user's workspace memberships
      const { data: memberships, error: membershipsError } = await supabase
        .from('workspace_members')
        .select(`
          workspace_id,
          role,
          status,
          workspace:workspaces(id, name, status)
        `)
        .eq('user_id', user.id)
        .eq('status', 'active');

      if (membershipsError) {
        console.error('Error fetching workspace memberships:', membershipsError);
      }

      const membershipData = memberships || [];
      
      // Extract workspace IDs where user has admin/owner role
      const managedWorkspaceIds = membershipData
        .filter(m => ['owner', 'admin'].includes(m.role))
        .map(m => m.workspace_id);

      // Extract all accessible workspace IDs
      const accessibleWorkspaceIds = membershipData
        .map(m => m.workspace_id);

      // Helper functions
      const canManageWorkspace = (workspaceId: string) => {
        return isSuperAdmin || managedWorkspaceIds.includes(workspaceId);
      };

      const canViewWorkspace = (workspaceId: string) => {
        return isSuperAdmin || accessibleWorkspaceIds.includes(workspaceId);
      };

      const canInviteMembers = (workspaceId: string) => {
        return canManageWorkspace(workspaceId);
      };

      const canManageModules = (workspaceId: string) => {
        return canManageWorkspace(workspaceId);
      };

      return {
        isSuperAdmin,
        isOwner: isSuperAdmin,
        canCreateWorkspaces: isSuperAdmin || managedWorkspaceIds.length > 0,
        canManageAllWorkspaces: isSuperAdmin,
        canViewAllWorkspaces: isSuperAdmin,
        canManageWorkspace,
        canViewWorkspace,
        canInviteMembers,
        canManageModules,
        managedWorkspaceIds,
        accessibleWorkspaceIds,
        hasFullAccess: isSuperAdmin,
        roles: isSuperAdmin ? ['owner'] : membershipData.map(m => m.role),
        checkPermission: (permission: string) => isSuperAdmin || managedWorkspaceIds.length > 0
      };
    },
    enabled: !!user?.id,
    staleTime: 1000 * 60 * 5, // 5 minutes
    retry: 2
  });

  return {
    permissions,
    isLoading,
    error: error as Error | null
  };
}